import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import IconCar from 'assets/IconCar';
import ButtonPrimaryLink from 'components/buttons/ButtonPrimaryLink/styles';
import RestrictedArea from 'components/layout/RestrictedArea';
import {
  checkIfSauceEmployee,
  FEATURE_RESTRICTION_IDS,
  getBookADriverRestriction
} from 'helpers/constants/restrictions';
import useBookADriverIntro from 'components/BookADriverButton/useBookADriverIntro';
import {
  BookADriverButtonWrap,
  BookADriverNewPill,
  BookADriverTooltip,
  BookADriverTooltipActions,
  BookADriverTooltipBody,
  BookADriverTooltipPrimary,
  BookADriverTooltipSecondary,
  BookADriverTooltipTitle
} from 'components/BookADriverButton/styles';

const CREATE_ORDER_ROUTE = '/book-a-driver/create-order';

// Kept in sessionStorage, like the dashboard login: the tooltip shows once per
// session and comes back in the next one.
const dismissedKey = (userId) => `bad_orders_tooltip_dismissed:${userId}`;

const isDismissed = (userId) => {
  try {
    return sessionStorage.getItem(dismissedKey(userId)) === 'true';
  } catch (e) {
    return false;
  }
};

const markDismissed = (userId) => {
  try {
    sessionStorage.setItem(dismissedKey(userId), 'true');
  } catch (e) {
    // Storage can be unavailable; the tooltip then just comes back next visit.
  }
};

const capture = (event, properties) =>
  window?.posthog?.capture?.(event, properties);

/**
 * The Orders page's Book a Driver button. Behind `bad_orders_tooltip` it also
 * introduces Book a Driver to locations that have barely used it.
 */
// Rendered only inside RestrictedArea, so a user without Book a Driver triggers
// neither the order-count requests nor the tooltip events.
const BookADriverButtonContent = ({hidden, serviceFeatures}) => {
  const history = useHistory();
  const locationId = useSelector((state) => state.user?.locationId);
  const userId = useSelector((state) => state.user?.userData?._id);

  const isFlagOn = !!serviceFeatures?.includes(
    FEATURE_RESTRICTION_IDS.BAD_ORDERS_TOOLTIP
  );
  // The BFF owns the rule (flag + fewer than five orders); the flag check here
  // only spares locations without it a request.
  const isNew = useBookADriverIntro(locationId, isFlagOn);

  const [dismissed, setDismissed] = useState(() => isDismissed(userId));
  useEffect(() => setDismissed(isDismissed(userId)), [userId]);

  const showTooltip = isNew && !!userId && !dismissed && !hidden;

  const viewTracked = useRef(false);
  useEffect(() => {
    if (!showTooltip || viewTracked.current) return;
    viewTracked.current = true;
    capture('bad_tooltip_view', {location_id: locationId});
    // Shown once per session: it stays up for this visit but not the next ones,
    // even if the merchant never clicks it.
    markDismissed(userId);
  }, [showTooltip]);

  const dismiss = () => {
    setDismissed(true);
    markDismissed(userId);
  };

  const onCheckItOut = () => {
    capture('bad_tooltip_click', {
      location_id: locationId,
      source: 'check_it_out'
    });
    dismiss();
    history.push(CREATE_ORDER_ROUTE);
  };

  const onGotIt = () => {
    capture('bad_tooltip_dismiss', {location_id: locationId});
    dismiss();
  };

  const onButtonClick = () => {
    if (!showTooltip) return;
    capture('bad_tooltip_click', {location_id: locationId, source: 'button'});
    dismiss();
  };

  return (
    <BookADriverButtonWrap>
      <ButtonPrimaryLink
        data-gtm='bookADriverButton'
        to={CREATE_ORDER_ROUTE}
        className='ButtonMedium ButtonDriver'
        onClick={onButtonClick}
      >
        <IconCar data-gtm='bookADriverButton' />
        <span data-gtm='bookADriverButton'>Book a Driver</span>
        {isNew && <BookADriverNewPill>NEW</BookADriverNewPill>}
      </ButtonPrimaryLink>
      {showTooltip && (
        <BookADriverTooltip
          role='dialog'
          aria-label='New: book a driver for any order'
        >
          <BookADriverTooltipTitle>
            New: book a driver for any order
          </BookADriverTooltipTitle>
          <BookADriverTooltipBody>
            Missing item, extra drivers for a big order, pickup turned into
            delivery. Takes about 30 seconds.
          </BookADriverTooltipBody>
          <BookADriverTooltipActions>
            <BookADriverTooltipPrimary type='button' onClick={onCheckItOut}>
              Check it out
            </BookADriverTooltipPrimary>
            <BookADriverTooltipSecondary type='button' onClick={onGotIt}>
              Got it
            </BookADriverTooltipSecondary>
          </BookADriverTooltipActions>
        </BookADriverTooltip>
      )}
    </BookADriverButtonWrap>
  );
};

BookADriverButtonContent.propTypes = {
  hidden: PropTypes.bool.isRequired,
  serviceFeatures: PropTypes.arrayOf(PropTypes.string)
};

BookADriverButtonContent.defaultProps = {
  serviceFeatures: []
};

const BookADriverButton = ({hidden}) => {
  const serviceFeatures = useSelector(
    (state) => state.user?.activeLocation?.AllowedServiceFeatures
  );

  return (
    <RestrictedArea
      restrictions={[]}
      features={
        checkIfSauceEmployee()
          ? []
          : [getBookADriverRestriction(serviceFeatures)].filter(Boolean)
      }
    >
      <BookADriverButtonContent
        hidden={hidden}
        serviceFeatures={serviceFeatures}
      />
    </RestrictedArea>
  );
};

BookADriverButton.propTypes = {
  hidden: PropTypes.bool
};

BookADriverButton.defaultProps = {
  hidden: false
};

export default BookADriverButton;
